<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { TooltipContentEmits, TooltipContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const tooltipContentVariants = cva(
  'rounded-md bg-dark/90 p-3 text-xs text-left font-display font-normal text-white whitespace-normal leading-normal space-y-2 ring-0 shadow-none data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0 data-[state=instant-open]:fade-in-0',
  {
    variants: {
      size: {
        xs: 'max-w-[80px]',
        sm: 'max-w-[160px]',
        md: 'max-w-[240px]',
        lg: 'max-w-[440px]',
        xl: 'max-w-[640px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

export type TooltipSize = NonNullable<VariantProps<typeof tooltipContentVariants>['size']>

export type TooltipContentWithSizeProps = TooltipContentProps & {
  size?: TooltipSize
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { TooltipContent, TooltipPortal, useForwardPropsEmits } from 'reka-ui'
import { FLOATING_Z_TOOLTIP, useFloatingZ } from '#ui-kit/lib/useFloatingZ'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<TooltipContentWithSizeProps>(), {
  sideOffset: 4,
})
const emits = defineEmits<TooltipContentEmits>()

const delegated = reactiveOmit(props, 'class', 'size')
const forwarded = useForwardPropsEmits(delegated, emits)
const floatingZ = useFloatingZ({ fallback: FLOATING_Z_TOOLTIP })
</script>

<template>
  <TooltipPortal>
    <TooltipContent
      v-bind="forwarded"
      :class="cn(tooltipContentVariants({ size: props.size }), props.class)"
      :style="{ zIndex: floatingZ }"
    >
      <slot />
    </TooltipContent>
  </TooltipPortal>
</template>
